import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MenuItem } from 'primeng/api';
import { StepsModule } from 'primeng/steps';
import { RadioButtonModule } from 'primeng/radiobutton';

import { SharedModule } from 'src/app/shared/shared.module';
import { ContactsModule } from 'src/app/modules/contact/contacts.module';
import { SendFoundReportDialogComponent } from 'src/app/shared/components/send-found-report-dialog/send-found-report-dialog.component';
import { AnimalModel } from '../../models/animal.model';
import { AnimalWhereaboutsType } from '../../models/animal-wherabout-types.enum';
import { AnimalContactTypeEnum } from '../../models/contact-type.enum';
import { FoundReportPdfRepository, SendFoundReportPayload } from '../../states/pdf.repository';
import { PickupReportPreviewBuilderService } from '../../services/pickup-report-preview-builder.service';
import { AnimalWhereaboutsRepository } from '../../states/animal-whereabouts.repository';
import { AnimalSheltersRepository } from 'src/app/shared/states/animal-shelter.repository';
import { ToastService } from 'src/app/shared/services/toast.service';
import { TranslocoService } from '@jsverse/transloco';
import { ContactModel } from 'src/app/modules/contact/models/contact.model';
import { ContactRepository } from 'src/app/modules/contact/states/contact.repository';
import { FoundReportPreviewContactEntry } from '../../models/found-report-preview-payload.model';
import { Observable, take } from 'rxjs';

export type WhereAboutContactType = AnimalContactTypeEnum;

export interface WhereAboutContactRef {
  type: WhereAboutContactType;
  label?: string | null;
  isPrimary?: boolean;
}

export interface WhereAboutContactVm {
  ref: WhereAboutContactRef;
  contact: ContactModel;
  isExisting: boolean;
  isValid: boolean;
}

@Component({
  selector: 'app-pick-up-report-dialog',
  templateUrl: './pick-up-report-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    StepsModule,
    RadioButtonModule,
    ContactsModule,
    SendFoundReportDialogComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PickUpReportDialogComponent implements OnInit {
  @Input() animal!: AnimalModel;
  @Input() visible: boolean = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() completed = new EventEmitter<void>();

  stepItems: MenuItem[] = [];
  activeStepIndex = 0;

  formGroup!: FormGroup;
  reportPreviewBlob: any = null;
  isLoadingPreview = false;
  isSaving = false;

  whereAboutContacts: WhereAboutContactVm[] = [];

  whereAboutContactTypeOptions: Array<{ label: string; value: AnimalContactTypeEnum | null }> = [
    { label: '-', value: null},
    { label: 'Eigentümer', value: AnimalContactTypeEnum.OWNER },
    { label: 'Abholberechtigt', value: AnimalContactTypeEnum.AUTHORIZED_PICKUP },
  ];

  activeContactPanelIndex = '0';
  sendDialogVisible = false;
  savedWhereaboutId: number | null = null;
  sendFnBound: (id: number, payload: SendFoundReportPayload) => Observable<any>;

  constructor(
    private readonly _pdfRepo: FoundReportPdfRepository,
    private readonly _previewBuilder: PickupReportPreviewBuilderService,
    private readonly _whereaboutsRepo: AnimalWhereaboutsRepository,
    private readonly _shelterRepo: AnimalSheltersRepository,
    private readonly _contactRepo: ContactRepository,
    private readonly _toast: ToastService,
    private readonly _transloco: TranslocoService,
    private readonly _cdr: ChangeDetectorRef,
  ) {
    this.sendFnBound = (id, payload) => this._pdfRepo.sendPickupReport(id, payload);
  }

  ngOnInit(): void {
    this.stepItems = [
      { label: this._transloco.translate('pickUpReport.step.form') },
      { label: this._transloco.translate('pickUpReport.step.pdf') },
      { label: this._transloco.translate('pickUpReport.pdf.signatures') },
    ];

    this.formGroup = new FormGroup({
      pickUpDateTime: new FormControl<Date | null>(new Date(), Validators.required),
      careCondition: new FormControl<string[]>([]),
      bcsNutrition: new FormControl<string | null>(null),
      temperament: new FormControl<string[]>([]),
      pickupSignature: new FormControl<string | null>(null, Validators.required),
      employeeSignature: new FormControl<string | null>(null, Validators.required),
      signatureLocation: new FormControl<string | null>(null, Validators.required),
      signatureDate: new FormControl<Date | null>(new Date(), Validators.required),
    });

    this.addWhereAboutContact(AnimalContactTypeEnum.OWNER);
  }

  // ── Contact type helpers ──────────────────────────────────────────────────

  getWhereAboutContactTypeLabel(type: WhereAboutContactType): string {
    return this.whereAboutContactTypeOptions.find(x => x.value === type)?.label ?? type;
  }

  trackByWhereAboutContact = (index: number, vm: WhereAboutContactVm) =>
    `${index}-${vm.ref.type}-${vm.contact?.id ?? 'new'}-${vm.ref.label ?? ''}`;

  private createEmptyWhereAboutContact(type: WhereAboutContactType = AnimalContactTypeEnum.OWNER): WhereAboutContactVm {
    return {
      ref: { type, label: null, isPrimary: false },
      contact: {} as ContactModel,
      isExisting: false,
      isValid: false,
    };
  }

  // ── Contact list management ───────────────────────────────────────────────

  addWhereAboutContact(type: WhereAboutContactType = AnimalContactTypeEnum.OWNER): void {
    const vm = this.createEmptyWhereAboutContact(type);
    if (this.whereAboutContacts.length === 0) {
      vm.ref.isPrimary = true;
    }
    this.whereAboutContacts = [...this.whereAboutContacts, vm];
    this.activeContactPanelIndex = (this.whereAboutContacts.length - 1).toString();
    this._cdr.markForCheck();
  }

  removeWhereAboutContact(index: number): void {
    if (index < 0 || index >= this.whereAboutContacts.length) return;
    const next = this.whereAboutContacts.filter((_, i) => i !== index);
    if (next.length > 0 && !next.some(x => x.ref.isPrimary)) {
      next[0] = { ...next[0], ref: { ...next[0].ref, isPrimary: true } };
    }
    this.whereAboutContacts = next;
    this._cdr.markForCheck();
  }

  onWhereAboutContactTypeChanged(index: number, type: WhereAboutContactType): void {
    const next = [...this.whereAboutContacts];
    const cur = next[index];
    if (!cur) return;
    next[index] = { ...cur, ref: { ...cur.ref, type } };
    this.whereAboutContacts = next;
    this._cdr.markForCheck();
  }

  onWhereAboutContactLabelChanged(index: number, label: string): void {
    const next = [...this.whereAboutContacts];
    const cur = next[index];
    if (!cur) return;
    next[index] = { ...cur, ref: { ...cur.ref, label } };
    this.whereAboutContacts = next;
    this._cdr.markForCheck();
  }

  onWhereAboutValidationChanged(index: number, valid: boolean): void {
    const next = [...this.whereAboutContacts];
    const cur = next[index];
    if (!cur) return;
    next[index] = { ...cur, isValid: valid };
    this.whereAboutContacts = next;
    this._cdr.markForCheck();
  }

  onWhereAboutNewContactUpdated(index: number, contact: ContactModel): void {
    const next = [...this.whereAboutContacts];
    const cur = next[index];
    if (!cur) return;
    next[index] = { ...cur, contact };
    this.whereAboutContacts = next;
    this._cdr.markForCheck();
  }

  onWhereAboutExistingSelected(index: number, contact: ContactModel): void {
    const next = [...this.whereAboutContacts];
    const cur = next[index];
    if (!cur) return;
    next[index] = { ...cur, contact, isExisting: !!contact?.id };
    this.whereAboutContacts = next;
    this._cdr.markForCheck();
  }

  // ── Navigation ────────────────────────────────────────────────────────────

  isBcsValid(): boolean {
    const care = this.formGroup.get('careCondition')?.value;
    const nutrition = this.formGroup.get('bcsNutrition')?.value;
    const temperament = this.formGroup.get('temperament')?.value;
    return care?.length > 0 && !!nutrition && temperament?.length > 0;
  }

  hasValidContact(): boolean {
    return this.whereAboutContacts.some(vm => vm.isValid);
  }

  isStep1Valid(): boolean {
    return !!this.formGroup.get('pickUpDateTime')?.value
      && this.isBcsValid()
      && this.hasValidContact();
  }

  isPdfBlockValid(): boolean {
    const controls = ['pickupSignature', 'employeeSignature', 'signatureLocation', 'signatureDate'];
    return controls.every(k => !!this.formGroup.get(k)?.value);
  }

  nextStep(): void {
    if (this.activeStepIndex === 0) {
      if (!this.isStep1Valid()) return;

      const shelter = this._shelterRepo.getActiveAnimalShelter();
      if (!shelter) {
        console.error('No active shelter found');
        return;
      }

      const contacts: FoundReportPreviewContactEntry[] = this.whereAboutContacts
        .filter(vm => vm.isValid && vm.contact && Object.keys(vm.contact).length > 0)
        .map(vm => ({ contact: vm.contact, type: vm.ref.type }));

      const payload = this._previewBuilder.build({
        animal: this.animal,
        formValue: this.formGroup.getRawValue(),
        shelterId: shelter.id,
        contacts,
      });

      this.isLoadingPreview = true;
      this._cdr.markForCheck();

      this._pdfRepo.getPickupPreview(payload).pipe(take(1)).subscribe(blob => {
        this.isLoadingPreview = false;
        if (blob) {
          this.reportPreviewBlob = blob;
          this.activeStepIndex = 1;
        }
        this._cdr.markForCheck();
      });
      return;
    }

    if (this.activeStepIndex === 1) {
      this.activeStepIndex = 2;
      this._cdr.markForCheck();
    }
  }

  prevStep(): void {
    if (this.activeStepIndex === 2) {
      this.activeStepIndex = 1;
    } else if (this.activeStepIndex === 1) {
      this.reportPreviewBlob = null;
      this.activeStepIndex = 0;
    }
    this._cdr.markForCheck();
  }

  finish(openSendDialog = true): void {
    if (!this.isPdfBlockValid() || this.isSaving) return;

    this.isSaving = true;
    this._cdr.markForCheck();

    const fv = this.formGroup.getRawValue();

    this._resolveContacts().pipe(take(1)).subscribe(resolvedContacts => {
      const payload: any = {
        type: AnimalWhereaboutsType.FOUNDANIMAL_RETURN,
        date: fv.pickUpDateTime ?? null,
        dateCare: null,
        discoverySource: null,
        information: null,
        shelterChanged: false,
        pickupSignature: fv.pickupSignature ?? null,
        employeeSignature: fv.employeeSignature ?? null,
        signatureLocation: fv.signatureLocation ?? null,
        signatureDate: (fv.pickupSignature || fv.employeeSignature) ? (fv.signatureDate ?? new Date()) : null,
        pickupDateTime: fv.pickUpDateTime ?? null,
        careCondition: fv.careCondition ?? [],
        bcsNutrition: fv.bcsNutrition ?? null,
        temperament: fv.temperament ?? [],
        animal: { id: this.animal.id },
        contacts: resolvedContacts,
      };

      this._whereaboutsRepo.create(payload).pipe(take(1)).subscribe({
        next: (result: any) => {
          this.isSaving = false;
          this.savedWhereaboutId = result?.id ?? null;
          this._toast.showSuccess(this._transloco.translate('pickUpReport.toast.saveSuccess'));
          if (openSendDialog) {
            this.sendDialogVisible = true;
          } else {
            this.completed.emit();
            this.close();
          }
          this._cdr.markForCheck();
        },
        error: () => {
          this.isSaving = false;
          this._cdr.markForCheck();
        }
      });
    });
  }

  private _resolveContacts(): Observable<Array<{ type: string; contact: number; label: string; isPrimary: boolean }>> {
    return new Observable(observer => {
      const vms = this.whereAboutContacts.filter(
        vm => vm.contact && Object.keys(vm.contact).length > 0
      );

      if (vms.length === 0) {
        observer.next([]);
        observer.complete();
        return;
      }

      const results: Array<{ type: string; contact: number; label: string; isPrimary: boolean }> = [];
      let done = 0;

      vms.forEach((vm, i) => {
        const persist = (id: number | null) => {
          if (id != null) {
            results[i] = {
              type: vm.ref.type,
              contact: id,
              label: vm.ref.label ?? '',
              isPrimary: vm.ref.isPrimary ?? false,
            };
          }
          done++;
          if (done === vms.length) {
            observer.next(results.filter(Boolean));
            observer.complete();
          }
        };

        const contactId = (vm.contact as any).id;
        if (contactId) {
          this._contactRepo.update(vm.contact, contactId).pipe(take(1))
            .subscribe(updated => persist(updated?.id ?? contactId));
        } else {
          this._contactRepo.create(vm.contact).pipe(take(1)).subscribe(created => persist(created?.id ?? null));
        }
      });
    });
  }

  onSendDialogClosed(): void {
    this.sendDialogVisible = false;
    this.completed.emit();
    this.close();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
